import { Injectable } from '@angular/core';

import { BehaviorSubject, EMPTY, Observable } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';

import { tokenGetter } from '../../main-module/declarations/mediego-jwt';

import { environment } from '../../../environments/environment';
import Timeout = NodeJS.Timeout;

export interface WebsocketConcurrencyMessage {
  engine: number;
  source: string;
  section: string;
  nbUsers: number;

  isInside?: boolean;
}

type ConcurrencyArea = Pick<WebsocketConcurrencyMessage, 'engine'|'source'|'section'>;

@Injectable({
  providedIn: 'root'
})
export class WebsocketService {

  private socket$: BehaviorSubject<WebSocketSubject<unknown>> = new BehaviorSubject<WebSocketSubject<unknown>>(undefined);

  private get socket(): WebSocketSubject<unknown> {
    return this.socket$.value;
  }

  private socketClosed$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  concurrency$: Observable<WebsocketConcurrencyMessage> = this.socket$.asObservable().pipe(
    filter((socket) => !!socket),
    switchMap((socket) => {

      return socket.multiplex(
        () => ({
          type: 'auth',
          token: tokenGetter()
        }),
        () => ({}),
        (message: WebsocketConcurrencyMessage) => message?.hasOwnProperty('section')
      ).pipe(
        map((message: WebsocketConcurrencyMessage) => ({
          ...message,
          isInside: this.currentSocketIsInsideSection(message.engine, message.source, message.section)
        })),
        catchError((err) => {
          console.error('socket error', err);
          // closing if not already closed
          if (!this.socketClosed$.value) this.socketClosed$.next(true);
          return EMPTY;
        })
      );

    })
  );

  private reconnectInterval: Timeout;
  private inside: ConcurrencyArea[] = [];

  static isSameArea(area1: ConcurrencyArea, area2: ConcurrencyArea) {
    return area1 && area2 && area1.engine === area2.engine && area1.source === area2.source && area1.section === area2.section;
  }

  constructor() {

    this.connect();

    this.socketClosed$.asObservable().pipe(
      map((closed) => {
        if (closed) {
          console.error('websocket connection closed');
          if (this.reconnectInterval) clearInterval(this.reconnectInterval);
          this.reconnectInterval = setInterval(() => {
            this.connect();
            console.warn('trying to reconnect after 7s...');
          }, 7000);
        } else if (this.reconnectInterval) clearInterval(this.reconnectInterval);
      })
    ).subscribe();
  }

  private connect() {
    this.socket$.next(
      webSocket({
        url: `${environment.websocketUrl}/2.0/concurrency`,
        openObserver: {
          next: () => {
            console.info('websocket connection established !');
            this.socketClosed$.next(false);
          }
        },
        closeObserver: {
          next: () => {
            // closing if not already closed
            if (!this.socketClosed$.value) this.socketClosed$.next(true);
          }
        }
      })
    );
  }

  enter(engine: number, source: string, section: string) {
    const currentArea: ConcurrencyArea = { engine, section, source };

    this.socket.next({
      type: 'concurrency',
      entering: true,
      ...currentArea
    });

    this.inside = [...this.inside, currentArea];
  }

  exit(engine: number, source: string, section: string) {
    const currentArea: ConcurrencyArea = { engine, section, source };

    this.socket.next({
      type: 'concurrency',
      entering: false,
      ...currentArea
    });

    this.inside = [...this.inside.filter(otherArea => !WebsocketService.isSameArea(currentArea, otherArea))];
  }

  currentSocketIsInsideSection(engine: number, source: string, section: string) {
    return !!this.inside?.find((otherArea) => WebsocketService.isSameArea({ engine, source, section }, otherArea));
  }

}
